import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '../../auth/useAuth';
import { createRawApiClient } from '../../api/client';
import { ME_QUERY_KEY, meQueryOptions } from '../../lib/meQuery';
import { onStepUpSucceeded } from '../../api/stepUp';
import type { MeResponse, UserPreferences } from '../../api/types';
import { workspaceKey } from '../../lib/workspaceQueryKey';
import { isAdvisorMembership } from '../../lib/personSelfLink';
import { useWorkspace } from '../../workspace/WorkspaceProvider';
import { workspaceKindFromPacks } from '../../workspace/useWorkspaceKind';
import {
  SELF_LINKED_PREFERENCE, mergeSelfLinked, runSelfLinkClaim, selfLinkDeclinedKey,
} from './selfLinkClaim';

/**
 * Mounts the silent onboarding self-link claim (`selfLinkClaim.ts`, steward-app#1851) in the
 * app shell, beside `useOnboardingTaxIdCopy`. Renders nothing and returns nothing.
 *
 * Eligibility is the tax-id copy's, plus one clause: the active FAMILY workspace of a person
 * `/me` lists onboarding invitations for, whose membership there is not an advisor connection
 * (appcore refuses an advisor with 403; the clause only saves the request).
 *
 * Two triggers: entering the workspace (and again when the set of inviting firms changes),
 * and every successful step-up ceremony (deferred by a macrotask so the ceremony's own
 * retry goes first). The claim uses the raw client, so `403 STEP_UP_REQUIRED` is an error
 * here and never a ceremony. `selfLinkClaim.ts` says which answers finish a pair and for how
 * long; nothing is ever shown and nothing is retried on a timer.
 */
export function useOnboardingSelfLink(): void {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const { activeWorkspaceId, activeMembership, profile, setPreferences } = useWorkspace();
  const meQuery = useQuery(meQueryOptions(token));

  const firmWorkspaceIds = (meQuery.data?.onboardingInvitations ?? []).map(i => i.firmWorkspaceId);
  const firmKey = [...new Set(firmWorkspaceIds)].sort().join(',');
  const eligible = !!token && !!activeWorkspaceId && !!activeMembership && firmKey !== ''
    && workspaceKindFromPacks(activeMembership.packs) !== 'firm'
    && !isAdvisorMembership(activeMembership.roleIds);

  const latest = useRef({ token, activeWorkspaceId, firmWorkspaceIds, eligible, profile, setPreferences });
  latest.current = { token, activeWorkspaceId, firmWorkspaceIds, eligible, profile, setPreferences };
  const running = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const settled = useRef(new Set<string>());

  const runRef = useRef<() => void>(() => undefined);
  runRef.current = () => {
    const cur = latest.current;
    if (!cur.eligible || !cur.token || !cur.activeWorkspaceId) return;
    if (running.current) { again.current = true; return; }
    const ws = cur.activeWorkspaceId;
    const raw = createRawApiClient(cur.token);
    const done = mergeSelfLinked(queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.[SELF_LINKED_PREFERENCE]
      ?? cur.profile?.preferences?.[SELF_LINKED_PREFERENCE], []);
    running.current = runSelfLinkClaim({
      workspaceId: ws,
      firmWorkspaceIds: cur.firmWorkspaceIds,
      done,
      settled: settled.current,
      // The whole body. The user id appcore writes is the token's; nothing here names one.
      claim: fw => raw.post<{ already?: boolean }>('/people/link-self/from-onboarding', { firmWorkspaceId: fw }, { workspaceId: ws }),
      markDone: async keys => {
        // Re-read at write time: appcore replaces a top-level preference whole.
        const now = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.[SELF_LINKED_PREFERENCE]
          ?? latest.current.profile?.preferences?.[SELF_LINKED_PREFERENCE];
        const patch: Partial<UserPreferences> = { [SELF_LINKED_PREFERENCE]: mergeSelfLinked(now, keys) };
        await latest.current.setPreferences(patch);
      },
      log: (event, fields) => { console.info('[onboarding-self-link]', event, fields); },
    }).then(result => {
      if (result.linked > 0) void queryClient.invalidateQueries({ queryKey: workspaceKey('people', ws) });
    }, () => undefined).finally(() => {
      running.current = null;
      if (again.current) { again.current = false; runRef.current(); }
    });
  };

  useEffect(() => {
    // No "already entered" guard: `settled` and the stored preference already skip every
    // finished pair, so a re-entry posts only for a pair still waiting on a step-up, or for
    // a firm whose invitation arrived since (`firmKey`).
    if (!eligible || !activeWorkspaceId) return;
    runRef.current();
  }, [eligible, activeWorkspaceId, firmKey]);

  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const off = onStepUpSucceeded(() => {
      const t = setTimeout(() => { timers.delete(t); runRef.current(); }, 0);
      timers.add(t);
    });
    return () => { off(); timers.forEach(clearTimeout); };
  }, []);
}

/**
 * Records that the signed-in person cleared their own link in a workspace, so the silent
 * claim above does not put it back. Best effort and never throws: a failed write leaves the
 * gap `selfLinkClaim.ts` names.
 */
export function useMarkSelfLinkDeclined(): (workspaceId: string) => void {
  const queryClient = useQueryClient();
  const { profile, setPreferences } = useWorkspace();
  const latest = useRef({ profile, setPreferences });
  latest.current = { profile, setPreferences };
  return useCallback((workspaceId: string) => {
    const now = queryClient.getQueryData<MeResponse>(ME_QUERY_KEY)?.preferences?.[SELF_LINKED_PREFERENCE]
      ?? latest.current.profile?.preferences?.[SELF_LINKED_PREFERENCE];
    const patch: Partial<UserPreferences> = { [SELF_LINKED_PREFERENCE]: mergeSelfLinked(now, [selfLinkDeclinedKey(workspaceId)]) };
    void Promise.resolve().then(() => latest.current.setPreferences?.(patch)).catch(() => undefined);
  }, [queryClient]);
}
